import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { Star, MapPin } from 'lucide-react';
import { getImageUrl } from '../services/api';
import { getCategoryPlaceholder } from '../utils/placeholders';
import L from 'leaflet';

// Fix Leaflet default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom marker icon matching the app theme
const createItemIcon = () => L.divIcon({
  html: `<div style="background:#D97757;width:32px;height:32px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
  </div>`,
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -34],
});

const ItemMapView = ({ items, userLocation }) => {
  const navigate = useNavigate();

  // Only show items that have coordinates
  const mappableItems = items.filter(
    (item) => item.owner_latitude && item.owner_longitude
  );

  // Determine map center: user location > first item > default (US center)
  const center = userLocation
    ? [userLocation.lat, userLocation.lng]
    : mappableItems.length > 0
    ? [mappableItems[0].owner_latitude, mappableItems[0].owner_longitude]
    : [39.8283, -98.5795];

  const zoom = userLocation ? 12 : mappableItems.length > 0 ? 12 : 4;

  if (mappableItems.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-[#EBE8DF] p-12 text-center" data-testid="map-no-items">
        <MapPin className="w-12 h-12 text-[#EBE8DF] mx-auto mb-3" />
        <p className="text-[#596652] font-medium">No items with location data</p>
        <p className="text-[#596652]/60 text-sm mt-1">Items need GPS coordinates to appear on the map</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden border border-[#EBE8DF] shadow-sm" data-testid="items-map-view">
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '520px', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {mappableItems.map((item) => {
          const imageUrl = item.images?.[0]
            ? getImageUrl(item.images[0])
            : getCategoryPlaceholder(item.category);

          return (
            <Marker
              key={item.id}
              position={[item.owner_latitude, item.owner_longitude]}
              icon={createItemIcon()}
            >
              <Popup>
                <div
                  className="cursor-pointer w-52"
                  onClick={() => navigate(`/items/${item.id}`)}
                  data-testid={`map-popup-${item.id}`}
                >
                  <img
                    src={imageUrl}
                    alt={item.title}
                    className="w-full h-28 object-cover rounded-lg mb-2"
                    onError={(e) => { e.target.src = getCategoryPlaceholder(item.category); }}
                  />
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#8C9A76]/15 text-[#596652] mb-1">
                    {item.category}
                  </span>
                  <p className="font-semibold text-[#2C3329] text-sm leading-tight mb-1 line-clamp-1">
                    {item.title}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-[#D97757] font-semibold text-sm">
                      ${item.price_daily}/day
                    </span>
                    {item.owner_rating > 0 && (
                      <span className="flex items-center text-xs text-[#2C3329]">
                        <Star className="w-3 h-3 text-[#F59E0B] fill-current mr-0.5" />
                        {item.owner_rating.toFixed(1)}
                      </span>
                    )}
                  </div>
                  {item.distance_km != null && (
                    <p className="text-[10px] text-[#596652] mt-1">
                      {item.distance_km < 1.6
                        ? `${(item.distance_km * 0.621371).toFixed(1)} mi away`
                        : `${(item.distance_km * 0.621371).toFixed(1)} mi away`}
                    </p>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default ItemMapView;
